import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import SidebarLayout from '../components/SidebarLayout';
import { apiClient } from '../lib/api';

const mono = { fontFamily: "'Geist Mono', 'JetBrains Mono', monospace" };

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, green }) {
  return (
    <div style={{
      background: '#0a0a0a',
      border: '1px solid rgba(255,255,255,0.06)',
      padding: '18px 20px',
      flex: 1,
      borderRight: 'none',
    }}>
      <div style={{
        ...mono, fontSize: 9.5, fontWeight: 500,
        letterSpacing: '0.12em', textTransform: 'uppercase',
        color: '#3f3f46', marginBottom: 8,
      }}>{label}</div>
      <div style={{
        fontSize: 28, fontWeight: 700, letterSpacing: '-0.04em',
        lineHeight: 1, marginBottom: 4,
        color: green ? '#22c55e' : '#fff',
      }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: '#3f3f46' }}>{sub}</div>}
    </div>
  );
}

// ── Rate bar ─────────────────────────────────────────────────────────────────
function RateBar({ rate }) {
  const pct = Math.min(100, Math.max(0, rate));
  const color = pct > 30 ? '#ef4444' : pct > 15 ? '#f59e0b' : '#22c55e';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{
        flex: 1, height: 6, background: 'rgba(255,255,255,0.04)',
        borderRadius: 3, overflow: 'hidden', maxWidth: 80,
      }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ ...mono, fontSize: 12, color, minWidth: 42, textAlign: 'right' }}>
        {rate.toFixed(1)}%
      </span>
    </div>
  );
}

// ── Loading skeleton ─────────────────────────────────────────────────────────
function LoadingSkeleton() {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
        {[...Array(4)].map((_, i) => (
          <div key={i} style={{ background: '#0a0a0a', padding: '18px 20px', borderRight: i < 3 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
            <div style={{ height: 10, background: 'rgba(255,255,255,0.04)', borderRadius: 3, width: '60%', marginBottom: 10 }} />
            <div style={{ height: 28, background: 'rgba(255,255,255,0.04)', borderRadius: 3, width: '80%' }} />
          </div>
        ))}
      </div>
      <div style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden' }}>
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{ height: 46, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', padding: '0 16px', gap: 16 }}>
            <div style={{ height: 12, background: 'rgba(255,255,255,0.04)', borderRadius: 3, flex: 2 }} />
            <div style={{ height: 12, background: 'rgba(255,255,255,0.04)', borderRadius: 3, width: 60 }} />
            <div style={{ height: 12, background: 'rgba(255,255,255,0.04)', borderRadius: 3, width: 80 }} />
          </div>
        ))}
      </div>
    </>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function Signals() {
  const { data, isLoading } = useQuery({
    queryKey: ['signals-summary'],
    queryFn: () => apiClient.getSignalsSummary(168),
  });

  const [expandedGoal, setExpandedGoal] = useState(null);

  const goals = data?.goals || [];
  const totalSignals = data?.total_signals ?? goals.reduce((s, g) => s + (g.total_runs || 0), 0);
  const activeGoals = goals.length;

  // Weighted averages
  let totalRuns = 0;
  let weightedReprompt = 0;
  let weightedAcceptance = 0;
  goals.forEach(g => {
    const runs = g.total_runs || 0;
    totalRuns += runs;
    weightedReprompt += runs * (g.reprompt_rate || 0);
    weightedAcceptance += runs * (g.acceptance_rate || 0);
  });
  const avgReprompt = totalRuns > 0 ? weightedReprompt / totalRuns : 0;
  const avgAcceptance = totalRuns > 0 ? weightedAcceptance / totalRuns : 0;

  // Sort goals by total_runs desc
  const sortedGoals = [...goals].sort((a, b) => (b.total_runs || 0) - (a.total_runs || 0));

  return (
    <SidebarLayout>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#000' }}>

        {/* Page header */}
        <div style={{
          height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 28px', borderBottom: '1px solid rgba(255,255,255,0.06)',
          background: '#0a0a0a', flexShrink: 0,
        }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Signal Quality</div>
          <span style={{ ...mono, fontSize: 11, color: '#3f3f46' }}>Last 7 days</span>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>

          {isLoading && <LoadingSkeleton />}

          {!isLoading && goals.length === 0 && (
            <div style={{
              background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 8, padding: '60px 20px', textAlign: 'center',
            }}>
              <div style={{ ...mono, fontSize: 13, color: '#52525b', marginBottom: 8 }}>
                No behavioral signals yet.
              </div>
              <div style={{ ...mono, fontSize: 11.5, color: '#3f3f46' }}>
                Add report_pipeline() calls to start capturing.
              </div>
            </div>
          )}

          {!isLoading && goals.length > 0 && (
            <>
              {/* ── PANEL 1: KPI Strip ──────────────────────────────────────── */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(4,1fr)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 8, overflow: 'hidden', marginBottom: 20,
              }}>
                <KpiCard label="Total Signals" value={totalSignals.toLocaleString()} sub="Last 7 days" />
                <KpiCard
                  label="Avg Re-prompt Rate"
                  value={`${avgReprompt.toFixed(1)}%`}
                  green={avgReprompt < 15}
                />
                <KpiCard
                  label="Avg Acceptance Rate"
                  value={`${avgAcceptance.toFixed(1)}%`}
                  green={avgAcceptance >= 70}
                />
                <KpiCard label="Active Goals" value={activeGoals} />
              </div>

              {/* ── PANEL 2: Per-goal table ─────────────────────────────────── */}
              <div style={{
                background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 8, overflow: 'hidden', marginBottom: 20,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Per-Goal Breakdown</div>
                  <span style={{ ...mono, fontSize: 11, color: '#3f3f46' }}>
                    {sortedGoals.length} goal{sortedGoals.length !== 1 ? 's' : ''} · click row to expand
                  </span>
                </div>

                {/* Column headers */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 100px 140px 140px 1.2fr',
                  padding: '9px 16px',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>
                  {['Goal', 'Total Runs', 'Re-prompt Rate', 'Acceptance Rate', 'Top Failing Dim'].map((h, i) => (
                    <span key={h} style={{
                      ...mono, fontSize: 9.5, fontWeight: 500,
                      letterSpacing: '0.1em', textTransform: 'uppercase',
                      color: '#3f3f46',
                      textAlign: i >= 1 ? 'right' : 'left',
                    }}>{h}</span>
                  ))}
                </div>

                {/* Rows */}
                {sortedGoals.map((goal) => {
                  const isExpanded = expandedGoal === goal.goal;
                  const models = goal.model_breakdown || [];
                  return (
                    <React.Fragment key={goal.goal}>
                      <div
                        onClick={() => setExpandedGoal(isExpanded ? null : goal.goal)}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '2fr 100px 140px 140px 1.2fr',
                          padding: '0 16px', height: 46, alignItems: 'center',
                          borderBottom: '1px solid rgba(255,255,255,0.04)',
                          cursor: 'pointer', transition: 'background 0.1s',
                          background: isExpanded ? 'rgba(255,255,255,0.02)' : 'transparent',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.025)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = isExpanded ? 'rgba(255,255,255,0.02)' : 'transparent'; }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{
                            fontSize: 13, color: '#fff',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            <span style={{ ...mono, fontSize: 11, color: '#52525b', marginRight: 6 }}>
                              {isExpanded ? '▾' : '▸'}
                            </span>
                            {goal.goal}
                          </div>
                        </div>
                        <div style={{ ...mono, fontSize: 13, color: '#a1a1aa', textAlign: 'right' }}>
                          {(goal.total_runs || 0).toLocaleString()}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <RateBar rate={goal.reprompt_rate || 0} />
                        </div>
                        <div style={{
                          ...mono, fontSize: 13, fontWeight: 600, textAlign: 'right',
                          color: (goal.acceptance_rate || 0) >= 70 ? '#22c55e' : (goal.acceptance_rate || 0) >= 50 ? '#f59e0b' : '#ef4444',
                        }}>
                          {(goal.acceptance_rate || 0).toFixed(1)}%
                        </div>
                        <div style={{
                          ...mono, fontSize: 11, color: '#a1a1aa', textAlign: 'right',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {goal.top_failing_dimension || '—'}
                        </div>
                      </div>

                      {/* Expanded: model breakdown */}
                      {isExpanded && models.length > 0 && (
                        <div style={{
                          background: 'rgba(255,255,255,0.015)',
                          borderBottom: '1px solid rgba(255,255,255,0.06)',
                          padding: '8px 16px 8px 40px',
                        }}>
                          <div style={{
                            ...mono, fontSize: 9.5, fontWeight: 500,
                            letterSpacing: '0.1em', textTransform: 'uppercase',
                            color: '#3f3f46', marginBottom: 8,
                          }}>Model Breakdown</div>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1.5fr 80px 100px 100px',
                            gap: '4px 0',
                          }}>
                            {/* Sub-header */}
                            {['Model', 'Runs', 'Re-prompt', 'Acceptance'].map((h, i) => (
                              <span key={h} style={{
                                ...mono, fontSize: 9, color: '#3f3f46',
                                textAlign: i >= 1 ? 'right' : 'left',
                                paddingBottom: 4,
                              }}>{h}</span>
                            ))}
                            {models.map((m) => (
                              <React.Fragment key={m.model_id}>
                                <span style={{ ...mono, fontSize: 11.5, color: '#a1a1aa' }}>{m.model_id}</span>
                                <span style={{ ...mono, fontSize: 11.5, color: '#a1a1aa', textAlign: 'right' }}>
                                  {(m.runs || 0).toLocaleString()}
                                </span>
                                <span style={{
                                  ...mono, fontSize: 11.5, textAlign: 'right',
                                  color: (m.reprompt_rate || 0) > 30 ? '#ef4444' : (m.reprompt_rate || 0) > 15 ? '#f59e0b' : '#22c55e',
                                }}>
                                  {(m.reprompt_rate || 0).toFixed(1)}%
                                </span>
                                <span style={{
                                  ...mono, fontSize: 11.5, textAlign: 'right',
                                  color: (m.acceptance_rate || 0) >= 70 ? '#22c55e' : '#f59e0b',
                                }}>
                                  {(m.acceptance_rate || 0).toFixed(1)}%
                                </span>
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      )}
                      {isExpanded && models.length === 0 && (
                        <div style={{
                          background: 'rgba(255,255,255,0.015)',
                          borderBottom: '1px solid rgba(255,255,255,0.06)',
                          padding: '12px 16px 12px 40px',
                          ...mono, fontSize: 11, color: '#52525b',
                        }}>
                          No model breakdown available
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* ── PANEL 3: Signal source breakdown ────────────────────────── */}
              <div style={{
                background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 8, overflow: 'hidden',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Signal Sources</div>
                  <span style={{ ...mono, fontSize: 11, color: '#3f3f46' }}>
                    Channel contribution by goal
                  </span>
                </div>

                {/* Column headers */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1.8fr 1fr 1fr 1fr 1fr',
                  padding: '9px 16px',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>
                  {['Goal', 'Turn Capture', 'Action Obs.', 'Explicit Feedback', 'Terminal Implicit'].map((h, i) => (
                    <span key={h} style={{
                      ...mono, fontSize: 9.5, fontWeight: 500,
                      letterSpacing: '0.1em', textTransform: 'uppercase',
                      color: '#3f3f46',
                      textAlign: i >= 1 ? 'right' : 'left',
                    }}>{h}</span>
                  ))}
                </div>

                {/* Rows */}
                {sortedGoals.map((goal) => {
                  const sources = goal.signal_sources || {};
                  const turn = sources.turn_capture || 0;
                  const action = sources.action_observation || 0;
                  const explicit = sources.explicit_feedback || 0;
                  const terminal = sources.terminal_implicit || 0;
                  const total = turn + action + explicit + terminal;

                  const pct = (v) => total > 0 ? `${((v / total) * 100).toFixed(0)}%` : '—';
                  const bar = (v) => {
                    const width = total > 0 ? (v / total) * 100 : 0;
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                        <div style={{
                          width: 48, height: 4, background: 'rgba(255,255,255,0.04)',
                          borderRadius: 2, overflow: 'hidden',
                        }}>
                          <div style={{ width: `${width}%`, height: '100%', background: '#60a5fa', borderRadius: 2 }} />
                        </div>
                        <span style={{ ...mono, fontSize: 11.5, color: '#a1a1aa', minWidth: 32, textAlign: 'right' }}>
                          {pct(v)}
                        </span>
                      </div>
                    );
                  };

                  return (
                    <div
                      key={goal.goal}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1.8fr 1fr 1fr 1fr 1fr',
                        padding: '0 16px', height: 42, alignItems: 'center',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                      }}
                    >
                      <div style={{
                        ...mono, fontSize: 12, color: '#fff',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {goal.goal}
                      </div>
                      {bar(turn)}
                      {bar(action)}
                      {bar(explicit)}
                      {bar(terminal)}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </SidebarLayout>
  );
}
